// https://stackoverflow.blog/2022/12/26/why-the-number-input-is-the-worst-input/
// https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
import allCombosHandsIdxs from '@/store/solfigr/allCombosHandsIdxs.json'
import { numFixed, SNK } from '@/helpers/helpers'
import { numFixedUnit } from '@gto/helpers/number-formatting'
import { unitDecimals } from '@/helpers/constants'
import * as R from 'remeda'

function getClonedBetSizes({ sizes, street_from, street_to, player_from, player_to }) {
  sizes = JSON.parse(JSON.stringify(sizes))

  const from = sizes[street_from][player_from]
  const from_dynamic = from.dynamic
  const from_fixed = from.fixed

  const to = sizes[street_to][player_to]
  const to_dynamic = to.dynamic
  const to_fixed = to.fixed

  const diffs = {
    player_from,
    player_to,
    total: 0,
    mode: {
      from: '',
      to: '',
    },
    dynamic: {
      from: {},
      to: {},
    },
    fixed: {
      from: {},
      to: {},
    },
  }

  if (to.mode !== from.mode) {
    diffs.mode.from = from.mode
    diffs.mode.to = to.mode
    diffs.total += 1
  }
  to.mode = from.mode

  Object.keys(to_dynamic).forEach(key => {
    if (key in from_dynamic || key === 'donk_count' || key === 'donk_sizes') {
      let fromKey = key
      let toKey = key

      // donksizes copy from betsizes
      if (key === 'donk_count') {
        toKey = 'donk_count'
        fromKey = 'bet_count'
      }
      if (key === 'donk_sizes') {
        toKey = 'donk_sizes'
        fromKey = 'bet_sizes'
      }
      if (to_dynamic[toKey].toString() !== from_dynamic[fromKey].toString()) {
        diffs.dynamic.from[toKey] = from_dynamic[fromKey]
        diffs.dynamic.to[toKey] = to_dynamic[fromKey]
        diffs.total += 1
      }
      to_dynamic[toKey] = from_dynamic[fromKey]
    }
  })
  Object.keys(to_fixed).forEach(key => {
    if (key in from_fixed || key === 'donk_count' || key === 'donk_sizes') {
      let fromKey = key
      let toKey = key

      // donksizes copy from betsizes
      if (key === 'donk_count') {
        toKey = 'donk_count'
        fromKey = 'bet_count'
      }
      if (key === 'donk_sizes') {
        toKey = 'donk_sizes'
        fromKey = 'bet_sizes'
      }
      // console.log('key', key, to_fixed[key])

      if (to_fixed[toKey].toString() !== from_fixed[fromKey].toString()) {
        diffs.fixed.from[toKey] = from_fixed[fromKey]
        diffs.fixed.to[toKey] = to_fixed[fromKey]
        diffs.total += 1
      }
      to_fixed[toKey] = from_fixed[fromKey]
    }
  })

  return { sizes, diffs }
}

const preflop_BB = 1
const flop_stackPotRatioValue = 125

const preflop_stackAnteBlindCheck = ({ stack, ante, preflop_SB }) =>
  stack >= ante + preflop_BB + preflop_SB
const flop_stackPotCheck = ({ stack, pot }) => stack / pot <= flop_stackPotRatioValue

// logarithmic slider
// https://stackoverflow.com/questions/846221/logarithmic-slider
const logposition = ({ value, min, max }) => {
  // position will be between 0 and 100
  const minp = 0
  const maxp = 1
  // The result should be between min an max
  const minv = Math.log(min)
  const maxv = Math.log(max)
  // calculate adjustment factor
  const scale = (maxv - minv) / (maxp - minp)
  return (Math.log(value) - minv) / scale + minp
}
const logslider = ({ position, min, max }) => {
  // position will be between 0 and 100
  const minp = 0
  const maxp = 1
  // The result should be between min an max
  const minv = Math.log(min)
  const maxv = Math.log(max)
  // calculate adjustment factor
  const scale = (maxv - minv) / (maxp - minp)
  return Math.exp(minv + scale * (position - minp))
}

function getSolfigrFields() {
  const stack = {
    id: 'stack',
    min: 1,
    max_preflop: 250,
    max: 999,
    // max: 250,
    step: 0.5,
    decimals: unitDecimals.bb,
    inputmode: 'decimal',
    preflop_stackAnteBlindCheck,
    flop_stackPotCheck,
    flop_stackPotRatioValue,
    default: 100,
    unit: 'bb',
  }

  const pot = {
    id: 'pot',
    min: 2,
    max: 999,
    step: 0.5,
    decimals: unitDecimals.bb,
    inputmode: 'decimal',
    flop_stackPotCheck,
    flop_stackPotRatioValue,
    default: 5.5,
  }

  const rakePercent = {
    // pct_of_pot
    id: 'rakePercent',
    min: 0,
    max: 100,
    step: 1, // min 1 cos bb_pot rounds to 1
    decimals: unitDecimals.rake_percent,
    inputmode: 'decimal',
  }
  const rakeCap = {
    // cap_in_chips
    id: 'rakeCap',
    min: 0,
    max: 9999,
    step: 0.5,
    decimals: unitDecimals.rake_cap,
    inputmode: 'decimal',
  }

  const ante = {
    id: 'ante',
    min: 0,
    max: 999,
    step: 0.5,
    decimals: unitDecimals.ante,
    inputmode: 'decimal',
    default: 0,
  }
  const blind = {
    id: 'blind',
    min: 0,
    max: 1,
    step: 0.1,
    decimals: unitDecimals.blind,
    inputmode: 'decimal',
    default: 0.5,
  }

  const allin_threshold = {
    id: 'allin_threshold',
    min: 0,
    max: 100,
    default: 67,
    step: 1,
    decimals: 0,
    inputmode: 'numeric',
  }
  const allin_if_less_than = {
    id: 'allin_if_less_than',
    min: 0,
    max: 20000,
    default: 500,
    step: 1,
    decimals: 0,
    inputmode: 'numeric',
  }
  const merge_sizes_threshold = {
    id: 'merge_sizes_threshold',
    min: 0,
    max: 1000,
    default_flop: 10,
    default_preflop: 5,
    step: 1,
    decimals: 0,
    inputmode: 'numeric',
  }
  const max_num_raises = {
    id: 'max_num_raises',
    min: 1,
    max: 10,
    // default_flop: 10,
    // default_preflop: 5,
    default: 5,
    step: 1,
    decimals: 0,
    inputmode: 'numeric',
  }
  const bet_count = {
    id: 'bet_count',
    min: 0,
    max: 2,
    max_river: 4,
    default: 1,
    decimals: 0,
    inputmode: 'numeric',
    message: num => (num === 0 ? 'force_check' : ''),
  }
  const raise_count = {
    id: 'raise_count',
    min: 0,
    max: 1,
    max_river: 3,
    default: 1,
    decimals: 0,
    inputmode: 'numeric',
    message: num => (num === 0 ? 'force_callfold' : ''),
  }
  const donk_count = {
    id: 'donk_count',
    min: 0,
    max: 2,
    max_river: 4,
    default: 1,
    decimals: 0,
    inputmode: 'numeric',
    message: num => (num === 0 ? 'force_check' : ''),
  }
  const second_raise_count = {
    id: 'second_raise_count',
    min: 0,
    max: 1,
    default: 1,
    decimals: 0,
    inputmode: 'numeric',
    message: num => (num === 0 ? 'force_callfold' : ''),
  }
  const third_plus_raise_count = {
    id: 'third_plus_raise_count',
    min: 0,
    max: 1,
    default: 1,
    decimals: 0,
    inputmode: 'numeric',
    message: num => (num === 0 ? 'force_callfold' : ''),
  }

  const brdCheckParser = ({ item, inputConf, oldVal, street }) => {
    let val = inputConf.origVal
    let num = inputConf.numVal

    val = `${val || item.size.min}`

    // sanitize b1 to 1b from Engine
    if (oldVal.includes('b')) {
      oldVal = `${oldVal.replace('b', '')}b`
    }

    const oldUnit = oldVal.length ? oldVal[oldVal.length - 1] : '%'

    const unitPart = val.replace(`${num}`, '')
    let unit = unitPart.length ? unitPart : oldUnit

    const units = item[`units_${street === 'preflop' ? 'preflop' : 'flop'}`]
    unit = units.includes(unit) ? unit : oldUnit

    const size = item[`size_${street === 'preflop' ? 'preflop' : 'flop'}`]
    const sizeUnit = size.by_unit.find(u => u.unit === unit)

    num = num < sizeUnit.min ? sizeUnit.min : num > sizeUnit.max ? sizeUnit.max : num

    if (sizeUnit.decimals === 0) {
      num = Number(num.toFixed())
    }

    const numStr = `${num}${unit}`
    const res = {
      inputConf,
      val,
      unit,
      num,
      numStr,
    }
    // console.log(res)
    return res
  }
  const brdCommon = hasX => ({
    min_length: 0,
    max_length: 6,
    units_preflop: [
      'b', //
      '%',
      'x',
      'e',
    ].filter(i => i),
    units_flop: ['%', hasX ? 'x' : null, 'e'].filter(i => i),
    inputmode: 'text',
    size_preflop: {
      // min: 1,
      parser: brdCheckParser,
      by_unit: [
        {
          unit: 'b',
          max: 250,
          min: 2,
          step: 1,
          decimals: 2,
        },
        {
          unit: '%',
          max: 25000,
          min: 1,
          step: 1,
          decimals: 2,
        },
        {
          unit: 'x',
          max: 250,
          min: 1,
          step: 0.5,
          decimals: 2,
        },
        {
          unit: 'e',
          max: 10,
          min: 1,
          step: 1,
          decimals: 2,
        },
      ],
    },
    size_flop: {
      // min: 1,
      parser: brdCheckParser,
      by_unit: [
        {
          unit: '%',
          max: 10000,
          min: 1,
          step: 1,
          decimals: 2,
        },
        ...(hasX
          ? [
              {
                unit: 'x',
                max: 10000,
                min: 1,
                step: 0.5,
                decimals: 2,
              },
            ]
          : []),
        {
          unit: 'e',
          max: 10,
          min: 1,
          step: 1,
          decimals: 2,
        },
      ],
    },
  })
  const bet_sizes = {
    id: 'bet_sizes',
    ...brdCommon(),
    message: sizes => (!sizes.length ? 'force_check' : ''),
    preflop_default: ['2x'],
    default: ['66%'],
  }
  const raise_sizes = {
    id: 'raise_sizes',
    ...brdCommon('hasX'),
    message: sizes => (!sizes.length ? 'force_callfold' : ''),
    max_length: 4,
    preflop_default: ['2x'],
    default: ['66%'],
  }
  const second_raise_sizes = {
    id: 'second_raise_sizes',
    ...brdCommon('hasX'),
    message: sizes => (!sizes.length ? 'force_callfold' : ''),
    max_length: 4,
    preflop_default: ['2x'],
    default: ['66%'],
  }
  const third_plus_raise_sizes = {
    id: 'third_plus_raise_sizes',
    ...brdCommon(),
    message: sizes => (!sizes.length ? 'force_callfold' : ''),
    max_length: 4,
    preflop_default: ['2x'],
    default: ['66%'],
  }
  const donk_sizes = {
    id: 'donk_sizes',
    ...brdCommon(),
    message: sizes => (!sizes.length ? 'force_check' : ''),
    preflop_default: ['2x'],
    default: ['66%'],
  }

  const add_allin = {
    id: 'add_allin',
    default: false,
  }

  const preflopRakeType = {
    id: 'preflopRakeType',
    default: 'no_flop_no_drop', // no_flop_no_drop, gg_network, full
  }

  // ICM ---
  const total_entrants = {
    id: 'total_entrants',
    min: 2,
    max: 4096,
    default: 2,
    step: 1,
    decimals: 0,
    inputmode: 'numeric',
  }

  const payout_prize = {
    id: 'payout_prize',
    min: 0.01,
    max: 100000000,
    default: 2,
    step: 0.01,
    decimals: 2,
    inputmode: 'numeric',
  }

  const tournament_bounty_types = {
    id: 'tournament_bounty_types',
    default: 'KO',
    options: [
      'KO', // Bounty + Knock-Out
      'PKO', // Bounty + Progressive Knock-Out
      'TKO', // Bounty + Total Knock Out
    ],
  }
  const tournament_types = {
    id: 'tournament_types',
    default: 'NORMAL',
    options: [
      'NORMAL', // Regular
      ...tournament_bounty_types.options,
    ],
  }
  const payout_levels = {
    id: 'payout_levels',
    getMin: type => (type === 'TKO' ? 0 : 1),
    max: 4096,
    default: [...new Array(3)].map((i, idx) => ({
      rank: idx + 1,
      prize: payout_prize.min,
      prize_pool_fraction: (3 * payout_prize.min) / 3,
      bounty: null,
    })),
    is_active: type => type !== 'TKO',
  }

  const stacks_distribution_type = {
    id: 'stacks_distribution_type',
    default: 'sdt_manual',
    options: ['sdt_manual', 'sdt_lognormal'],
  }

  const num_of_players_remaining = {
    id: 'num_of_players_remaining',
    min: 2,
    max: 4094,
    default: 2,
    step: 1,
    decimals: 0,
    inputmode: 'numeric',
  }
  const remaining_stack = {
    id: 'remaining_stack',
    min: 0.001,
    max: Infinity,
    step: 1,
    decimals: 3,
    inputmode: 'numeric',
  }

  const avg_stack = {
    id: 'avg_stack',
    min: 1,
    // max: Infinity,
    max: 708, // hack to prevent api 500 error
    getDefault: ({ stacks }) => R.sum(stacks) / stacks.length,
    step: 0.5,
    decimals: unitDecimals.bb,
    inputmode: 'decimal',
  }
  const pot_size = {
    ...pot,
    id: 'pot_size',
  }
  const avg_bounty = {
    id: 'avg_bounty',
    min: 0.01,
    max: Infinity,
    step: 0.5,
    getDefault: icmHandDetails =>
      !icmHandDetails
        ? 2
        : ['bounty_total_IP', 'bounty_total_OOP'].reduce(
            (acc, key) => acc + icmHandDetails[key] || 0,
            0,
          ) / 2 || 0.01,
    decimals: 2,
    inputmode: 'decimal',
    is_active: type => type !== 'NORMAL',
    unit: '$',
  }

  const bounty_instant_percentage = {
    id: 'bounty_instant_percentage',
    default: 0.5,
    inputFormatter: val => numFixed(val * 100),
    inputParser: val => val / 100,
    // after formating - handled on input
    min: 1,
    max: 100,
    step: 1,
    decimals: 0,
    inputmode: 'numeric',
    unit: '%',
    is_active: type => type === 'PKO',
  }

  const bounty_instant = {
    id: 'bounty_instant',
    min: 0.01,
    max: Infinity,
    default: 2,
    step: 0.5,
    decimals: 2,
    is_active: type => type === 'PKO',
    inputmode: 'decimal',
    unit: '$',
  }
  const bounty_total = {
    id: 'bounty_total',
    min: 0.01,
    max: Infinity,
    default: 2,
    step: 0.5,
    decimals: 2,
    inputmode: 'decimal',
    is_active: type => type !== 'NORMAL',
    unit: '$',
  }

  const fillLognormalDeviation = () => {
    const max = 1.5
    const def = 0.75
    return {
      max,
      default: def,
      getValueFromPercentage: value => (1 - value) * max,
      getPercentageFromValue: value => (value === null ? def : value) / max,
    }
  }

  const lognormal_deviation = {
    id: 'lognormal_deviation',
    ...fillLognormalDeviation(),
  }

  const stack_IP = {
    ...stack,
    id: 'stack_IP',
  }
  const stack_OOP = {
    ...stack,
    id: 'stack_OOP',
  }
  const stack_OOP1 = {
    ...stack,
    id: 'stack_OOP1',
  }

  const res = {
    stack,
    pot,
    ante,
    blind,

    allin_threshold,
    allin_if_less_than,
    merge_sizes_threshold,
    max_num_raises,

    bet_count,
    raise_count,
    second_raise_count,
    third_plus_raise_count,
    donk_count,
    bet_sizes,
    raise_sizes,
    second_raise_sizes,
    third_plus_raise_sizes,
    donk_sizes,

    add_allin,

    rakePercent,
    rakeCap,
    preflopRakeType,

    total_entrants,
    payout_prize,
    bounty_instant_percentage,
    stacks_distribution_type,
    tournament_types,
    tournament_bounty_types,
    payout_levels,
    num_of_players_remaining,
    remaining_stack,
    avg_stack,
    avg_bounty,
    pot_size,
    lognormal_deviation,
    bounty_instant,
    bounty_total,
    stack_IP,
    stack_OOP,
    stack_OOP1,
  }
  return res
}
function getSizesSkeleton() {
  const conf = getSolfigrFields()

  const base_dynBetsRaisesTurnRiverOOP = {
    bet_count: conf.bet_count.default,
    raise_count: conf.raise_count.default,
    donk_count: conf.donk_count.default,
  }
  const base_dynBetsRaisesTurnRiverIP = {
    bet_count: conf.bet_count.default,
    raise_count: conf.raise_count.default,
  }
  const base_dynBetsRaisesFlop = {
    bet_count: conf.bet_count.default,
    raise_count: conf.raise_count.default,
  }
  const base_dynBetsRaisesPreflop = {
    // same as flop but Iam not sure if this is ok
    bet_count: conf.bet_count.default,
    raise_count: conf.raise_count.default,
    second_raise_count: conf.second_raise_count.default,
    third_plus_raise_count: conf.third_plus_raise_count.default,
  }
  const base_useAdvancedTrue = { use_fixed_sizes: true }

  const base_mode = { mode: 'automatic' }

  const getPreflop = () => ({
    player_oop: {
      ...base_mode,
      dynamic: {
        bet_sizes: conf.bet_sizes.preflop_default,
        raise_sizes: conf.raise_sizes.preflop_default,
        add_allin: conf.add_allin.default,
        second_raise_sizes: conf.second_raise_sizes.preflop_default,
        third_plus_raise_sizes: conf.third_plus_raise_sizes.preflop_default,
        ...base_dynBetsRaisesPreflop,
        ...base_useAdvancedTrue,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.preflop_default,
        raise_sizes: conf.raise_sizes.preflop_default,
        second_raise_sizes: conf.second_raise_sizes.preflop_default,
        third_plus_raise_sizes: conf.third_plus_raise_sizes.preflop_default,
      },
    },
    player_ip: {
      ...base_mode,
      dynamic: {
        bet_sizes: conf.bet_sizes.preflop_default,
        raise_sizes: conf.raise_sizes.preflop_default,
        add_allin: conf.add_allin.default,
        second_raise_sizes: conf.second_raise_sizes.preflop_default,
        third_plus_raise_sizes: conf.third_plus_raise_sizes.preflop_default,
        ...base_dynBetsRaisesPreflop,
        ...base_useAdvancedTrue,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.preflop_default,
        raise_sizes: conf.raise_sizes.preflop_default,
        second_raise_sizes: conf.second_raise_sizes.preflop_default,
        third_plus_raise_sizes: conf.second_raise_sizes.preflop_default,
      },
    },
    player_oop1: {
      ...base_mode,
      dynamic: {
        bet_sizes: conf.bet_sizes.preflop_default,
        raise_sizes: conf.raise_sizes.preflop_default,
        add_allin: conf.add_allin.default,
        second_raise_sizes: conf.second_raise_sizes.preflop_default,
        third_plus_raise_sizes: conf.third_plus_raise_sizes.preflop_default,
        ...base_dynBetsRaisesPreflop,
        ...base_useAdvancedTrue,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.preflop_default,
        raise_sizes: conf.raise_sizes.preflop_default,
        second_raise_sizes: conf.second_raise_sizes.preflop_default,
        third_plus_raise_sizes: conf.third_plus_raise_sizes.preflop_default,
      },
    },
  })

  const getFlop = () => ({
    player_oop: {
      ...base_mode,
      dynamic: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
        add_allin: conf.add_allin.default,
        ...base_dynBetsRaisesFlop,
        ...base_useAdvancedTrue,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
      },
    },
    player_ip: {
      ...base_mode,
      dynamic: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
        add_allin: conf.add_allin.default,
        ...base_dynBetsRaisesFlop,
        ...base_useAdvancedTrue,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
      },
    },
    player_oop1: {
      ...base_mode,
      dynamic: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
        add_allin: conf.add_allin.default,
        ...base_dynBetsRaisesFlop,
        ...base_useAdvancedTrue,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
      },
    },
  })

  const getTurnRiver = () => ({
    player_oop: {
      ...base_mode,
      dynamic: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
        donk_sizes: conf.donk_sizes.default,
        add_allin: conf.add_allin.default,
        ...base_dynBetsRaisesTurnRiverOOP,
        ...base_useAdvancedTrue,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
        donk_sizes: conf.donk_sizes.default,
      },
    },
    player_ip: {
      ...base_mode,
      dynamic: {
        add_allin: conf.add_allin.default,
        ...base_dynBetsRaisesTurnRiverIP,
        ...base_useAdvancedTrue,
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
      },
    },
    player_oop1: {
      ...base_mode,
      dynamic: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
        donk_sizes: conf.donk_sizes.default,
        add_allin: conf.add_allin.default,
        ...base_dynBetsRaisesTurnRiverOOP,
        ...base_useAdvancedTrue,
      },
      fixed: {
        bet_sizes: conf.bet_sizes.default,
        raise_sizes: conf.raise_sizes.default,
        second_raise_sizes: conf.second_raise_sizes.default,
        donk_sizes: conf.donk_sizes.default,
      },
    },
  })

  const res = {
    preflop: getPreflop(),
    flop: getFlop(),
    turn: getTurnRiver(),
    river: getTurnRiver(),
  }
  return res
}
function getHandsRangeFromGroupRange({ groupRange }) {
  const res = []
  // "AA": [ 1, 2, 3... ],
  groupRange.forEach(i => {
    allCombosHandsIdxs[i.id].forEach(hIdx => {
      res[hIdx] = i.range
    })
  })
  return res
}
function getGroupsRangeFromHandsRange({ tableGroups, handsRange }) {
  const res = tableGroups.map(i => {
    const r = {
      id: i.id,
      range: handsRange && handsRange[i.combos[0].api_index],
      hand_count: i.hand_count,
      hands: i.combos.map(cc => cc.id),
    }
    return r
  })
  return res
}
function getRangeOverview({ groupRange }) {
  const totalCombos = groupRange.reduce((acc, group) => acc + group.hand_count * group.range, 0)
  const res = {
    combos: totalCombos,
    percentage: totalCombos / 1326,
  }
  return res
}
function getSolfigrPlayer({ tableGroups, api_player }) {
  if (!api_player) return undefined
  const groupRange = getGroupsRangeFromHandsRange({
    tableGroups,
    handsRange: api_player.range,
  })
  const { combos, percentage } = getRangeOverview({ groupRange })
  const res = {
    position: api_player.position,
    display_position:
      api_player.display_position || api_player.table_position || api_player.position,
    combos,
    percentage,
    groupRange,
  }
  return res
}
function getSolfigrCanRaises({ allow_limp, street, position }) {
  const isPreflop = street.toLowerCase() === 'preflop'

  const res = {
    canBet: isPreflop && !allow_limp ? position === 'player_ip' : true,
    canRaise: isPreflop ? allow_limp || position === 'player_oop' : true,
    canSecondRaise: !isPreflop || (isPreflop && (allow_limp || position === 'player_ip')),
    canSecondRaiseCount: isPreflop && (allow_limp || position === 'player_ip'),
    canThirdPlusRaise: isPreflop && (allow_limp || position === 'player_oop'),
    canDonk: isPreflop
      ? false
      : position === 'player_oop' && ['turn', 'river'].includes(street.toLowerCase()),
  }
  return res
}
function getStreetBet({ sizes, allow_limp, street, position }) {
  const {
    canBet, //
    canRaise,
    canSecondRaise,
    canSecondRaiseCount,
    canThirdPlusRaise,
    canDonk,
  } = getSolfigrCanRaises({ allow_limp, street, position })

  const player = sizes[street][position]
  const plMode = player.mode
  const plDynamic = player.dynamic
  const plFixed = player.fixed

  // engine takes 1b as b1
  const trnTo = (arr, key) =>
    arr ? arr.map(i => i && (i.includes('b') ? `b${i.replace('b', '')}` : i)) : undefined

  const getSizes = l_sizes => ({
    bet_sizes: canBet ? trnTo(l_sizes.bet_sizes) : [], // mandatory - minimal []
    raise_sizes: canRaise ? trnTo(l_sizes.raise_sizes) : [], // mandatory - minimal []

    donk_sizes: canDonk ? trnTo(l_sizes.donk_sizes) : undefined,
    second_raise_sizes: canSecondRaise ? trnTo(l_sizes.second_raise_sizes) : undefined,
    third_plus_raise_sizes: canThirdPlusRaise ? trnTo(l_sizes.third_plus_raise_sizes) : undefined,
  })

  const modes = {
    automatic: {},
    dynamic: {
      add_allin: plDynamic.add_allin,

      bet_count: plDynamic.bet_count || 0, // mandatory - minimal 0
      raise_count: plDynamic.raise_count || 0, // mandatory - minimal 0

      second_raise_count: canSecondRaiseCount ? plDynamic.second_raise_count : undefined,
      third_plus_raise_count: canThirdPlusRaise ? plDynamic.third_plus_raise_count : undefined,
      donk_count: canDonk ? plDynamic.donk_count : undefined,
      use_fixed_sizes: plDynamic.use_fixed_sizes,
      ...(plDynamic.use_fixed_sizes ? getSizes(plDynamic) : {}),
    },
    fixed: getSizes(plFixed),
  }

  const res = {
    position: position
      .replace('player_', '')
      .replace(/oop(\d+)/g, 'oop+$1')
      .toUpperCase(),
    type: player.mode.toUpperCase(),
    ...modes[plMode],
  }
  Object.keys(res).forEach(key => {
    if (res[key] === undefined) {
      delete res[key]
    }
  })
  return res
}

function getStreetBetSizes({ sizes, startStreet, allow_limp, players }) {
  const config = {
    ...(startStreet === 'preflop' ? { PREFLOP: players } : {}),
    FLOP: players,
    TURN: players,
    RIVER: players,
  }

  const res = Object.entries(config).map(([street, positions]) => ({
    street,
    position_bet_sizes: positions.map(position => ({
      ...getStreetBet({
        street: street.toLowerCase(), //
        sizes,
        allow_limp,
        position,
      }),
      allow_limp,
    })),
  }))

  return res
}

const getBlinds = (players = []) =>
  players.reduce(
    (acc, player) =>
      player.position === 'OOP' ? { ...acc, BB: player.blind } : { ...acc, SB: player.blind },
    {},
  )

const getPlayer = ({
  position,
  player,
  stack,
  blind,
  isPreflop,

  isIcmTournament,
  icmHandDetails,
  icmTournament,
}) => {
  const conf = getSolfigrFields()
  const res = {
    position,
    display_position: player.display_position,
    blind: isPreflop ? blind : null,
    range: isPreflop ? null : getHandsRangeFromGroupRange({ groupRange: player.groupRange }),

    stack: isIcmTournament ? icmHandDetails[`stack_${position}`] : stack,
    tournament_instant_bounty:
      isIcmTournament && conf.bounty_instant.is_active(icmTournament.type)
        ? icmHandDetails[`bounty_instant_${position}`]
        : null,
    tournament_total_bounty:
      isIcmTournament && conf.bounty_total.is_active(icmTournament.type)
        ? icmHandDetails[`bounty_total_${position}`]
        : null,
  }
  return res
}

const getTournamentApiData = ({ icmTournament: tt, icmPlayersAndStacks: pp }) => {
  const conf = getSolfigrFields()
  return {
    stacks_distribution_type: pp.stacks_distribution_type === 'sdt_manual' ? null : 'LogNormal',
    stacks: pp.stacks_distribution_type === 'sdt_manual' ? pp.stacks_manual : pp.stacks_defined,
    players_remaining: pp.num_of_players_remaining,
    average_bounty: conf.avg_bounty.is_active(tt.type) ? pp.avg_bounty : null,
    average_stack: pp.avg_stack,
    lognormal_deviation:
      pp.stacks_distribution_type === 'sdt_manual' ? null : pp.lognormal_deviation,
    tournament_structure: {
      name: '', // required on be
      bounty_instant_percentage: conf.bounty_instant_percentage.is_active(tt.type)
        ? tt.bounty_instant_percentage
        : null,
      type: tt.type,
      prize_pool: (conf.payout_levels.is_active(tt.type) && tt.prize_pool) || 0,
      currency: tt.currency,
      players_per_table: tt.players_per_table,
      total_entrants: tt.total_entrants,
      payout_levels:
        (conf.payout_levels.is_active(tt.type) &&
          tt.payout_levels.map(i => ({
            rank: i.rank,
            prize: i.prize,
          }))) ||
        [],
    },
  }
}

const getSolfigrFormApiPostData = ({
  startStreet,
  allin_threshold,
  allin_if_less_than,
  merge_sizes_threshold,
  max_num_raises,
  treeOperations,
  playerIP,
  playerOOP,
  playerOOP1,
  sizes,
  stack,
  stack_IP,
  stack_OOP,
  stack_OOP1,

  allowRake,
  rakeCap,
  rakePercent,
  preflopRakeType,
  pot,
  ante,
  ante_distribution_method,
  blind,
  allow_limp,

  tournamentType,
  icmTournament,
  icmPlayersAndStacks,
  icmHandDetails,
}) => {
  const isPreflop = startStreet === 'preflop'
  const isIcmTournament = !isPreflop && tournamentType === 'icm'

  stack = numFixedUnit(stack, 'bb') // on preflop + postflop
  stack_IP = numFixedUnit(stack_IP, 'bb')
  stack_OOP = numFixedUnit(stack_OOP, 'bb')
  stack_OOP1 = numFixedUnit(stack_OOP1, 'bb')

  pot = isPreflop ? 0 : numFixedUnit(isIcmTournament ? icmHandDetails.pot_size : pot, 'bb')
  blind = isPreflop ? numFixedUnit(blind, 'blind') : null
  ante = numFixedUnit(ante, 'ante')

  const street_bet_sizes = getStreetBetSizes({
    sizes,
    startStreet,
    allow_limp,
    players: playerOOP1 ? ['player_oop', 'player_oop1', 'player_ip'] : ['player_oop', 'player_ip'],
  })

  const res = {
    starting_street: startStreet.toUpperCase(),
    pot,
    ante: isPreflop ? ante : null,
    ante_distribution_method,
    bet_sizes: {
      allin_threshold,
      allin_if_less_than,
      merge_sizes_threshold,
      max_num_raises,
      street_bet_sizes,
    },
    players: [
      getPlayer({
        blind: 1,
        isPreflop,
        player: playerOOP,
        position: 'OOP',
        stack: playerOOP1 ? stack_OOP : stack,
        isIcmTournament,
        icmTournament,
        icmHandDetails,
      }), //
      getPlayer({
        blind,
        isPreflop,
        player: playerIP,
        position: 'IP',
        stack: playerOOP1 ? stack_IP : stack,
        isIcmTournament,
        icmTournament,
        icmHandDetails,
      }),
      playerOOP1 &&
        getPlayer({
          blind: 1,
          isPreflop,
          player: playerOOP1,
          position: 'OOP+1',
          stack: stack_OOP1,
          isIcmTournament,
          icmTournament,
          icmHandDetails,
        }),
    ].filter(Boolean),
    tree_operations: treeOperations,

    ...(isIcmTournament
      ? {}
      : {
          rake: {
            pct_of_pot: allowRake ? rakePercent : 0,
            cap_in_chips: allowRake ? rakeCap : 0,
            preflop_rake_type: allowRake ? preflopRakeType : null,
          },
        }),

    tournament_data: isIcmTournament
      ? getTournamentApiData({
          icmTournament,
          icmPlayersAndStacks,
        })
      : null,
  }

  // console.log('getSolfigrFormApiPostData--', res)
  return res
}

const getPreflopSizes = ({ sizes, player, allow_limp }) => {
  if (allow_limp) return sizes

  if (player === 'player_oop')
    return Object.keys(sizes).reduce((acc, size) => {
      if (
        [
          'second_raise_sizes', //
          'second_raise_count',
          'bet_sizes',
          'bet_count',
        ].includes(size)
      )
        return acc

      return {
        ...acc, //
        [size]: sizes[size],
      }
    }, {})

  return Object.keys(sizes).reduce((acc, size) => {
    if (
      [
        'raise_sizes', //
        'raise_count',
        'third_plus_raise_sizes',
        'third_plus_raise_count',
      ].includes(size)
    )
      return acc
    return {
      ...acc, //
      [size]: sizes[size],
    }
  }, {})
}

function getSolfigrSizesFromApiData({ street_bet_sizes }) {
  const res = getSizesSkeleton()

  street_bet_sizes.forEach(({ street, position_bet_sizes }) => {
    const { player_ip, player_oop, player_oop1 } = res[street.toLowerCase()]

    position_bet_sizes.forEach(({ position, type, ...betSizes }) => {
      const mode = type.toLowerCase()
      const player = position === 'IP' ? player_ip : position === 'OOP' ? player_oop : player_oop1

      player.mode = mode
      if (mode === 'automatic') {
        // do nothing
      } else if (mode === 'dynamic') {
        Object.keys(player[mode]).forEach(key => {
          if (key === 'use_fixed_sizes') {
            player[mode].use_fixed_sizes = betSizes[key]
          } else if (key.includes('sizes')) {
            player[mode][key] = betSizes[key] || []
          } else {
            player[mode][key] = betSizes[key] || 0
          }
        })
      } else if (mode === 'fixed') {
        Object.keys(player[mode]).forEach(key => {
          if (!(key in player[mode])) return
          player[mode][key] = betSizes[key] || []
        })
      }
    })
  })

  return res
}

function getFlopOnTurnAndRiverDiffs({ sizes, multiway = false }) {
  const { sizes: sizes_ip_flop_turn, diffs: diffs_ip_flop_turn } = getClonedBetSizes({
    sizes,
    street_from: 'flop',
    street_to: 'turn',
    player_from: 'player_ip',
    player_to: 'player_ip',
  })
  const { sizes: sizes_oop_flop_turn, diffs: diffs_oop_flop_turn } = getClonedBetSizes({
    sizes: sizes_ip_flop_turn,
    street_from: 'flop',
    street_to: 'turn',
    player_from: 'player_oop',
    player_to: 'player_oop',
  })

  const { sizes: sizes_oop1_flop_turn, diffs: diffs_oop1_flop_turn } = getClonedBetSizes({
    sizes: sizes_oop_flop_turn,
    street_from: 'flop',
    street_to: 'turn',
    player_from: 'player_oop1',
    player_to: 'player_oop1',
  })

  const { sizes: sizes_ip_flop_river, diffs: diffs_ip_flop_river } = getClonedBetSizes({
    sizes: multiway ? sizes_oop1_flop_turn : sizes_oop_flop_turn,
    street_from: 'flop',
    street_to: 'river',
    player_from: 'player_ip',
    player_to: 'player_ip',
  })
  const { sizes: sizes_oop_flop_river, diffs: diffs_oop_flop_river } = getClonedBetSizes({
    sizes: sizes_ip_flop_river,
    street_from: 'flop',
    street_to: 'river',
    player_from: 'player_oop',
    player_to: 'player_oop',
  })
  const { sizes: sizes_oop1_flop_river, diffs: diffs_oop1_flop_river } = getClonedBetSizes({
    sizes: sizes_oop_flop_river,
    street_from: 'flop',
    street_to: 'river',
    player_from: 'player_oop1',
    player_to: 'player_oop1',
  })

  const sizes_final = multiway ? sizes_oop1_flop_river : sizes_oop_flop_river
  const diffs_total =
    diffs_ip_flop_turn.total +
    diffs_oop_flop_turn.total +
    diffs_ip_flop_river.total +
    diffs_oop_flop_river.total +
    (multiway ? diffs_oop1_flop_turn.total + diffs_oop1_flop_river.total : 0)

  const res = {
    sizes_final,
    diffs_total,
    diffs_ip_flop_turn,
    diffs_oop_flop_turn,
    diffs_ip_flop_river,
    diffs_oop_flop_river,
  }

  res.diffs_oop1_flop_river = multiway ? diffs_oop1_flop_river : undefined
  res.diffs_oop1_flop_turn = multiway ? diffs_oop1_flop_turn : undefined

  return res
}

function getPlayersCloningFirstPlayer({ sizes, players }) {
  const res = {}
  for (let i = 0; i < players.length; i++) {
    const { diffs: diffs_flop } = getClonedBetSizes({
      sizes,
      street_from: 'flop',
      street_to: 'flop',
      player_from: players[i],
      player_to: 'player_oop',
    })
    const { diffs: diffs_turn } = getClonedBetSizes({
      sizes,
      street_from: 'turn',
      street_to: 'turn',
      player_from: players[i],
      player_to: 'player_oop',
    })
    const { diffs: diffs_river } = getClonedBetSizes({
      sizes,
      street_from: 'turn',
      street_to: 'turn',
      player_from: players[i],
      player_to: 'player_oop',
    })

    const diffs_total = diffs_flop.total + diffs_turn.total + diffs_river.total
    res[players[i]] = diffs_total === 0
  }
  return res
}

function getSizesDiffsOnAllStreets({ sizes, player_from, player_to }) {
  const { sizes: sizes_from_flop, diffs: diffs_from_flop } = getClonedBetSizes({
    sizes,
    street_from: 'flop',
    street_to: 'flop',
    player_from,
    player_to,
  })
  const { sizes: sizes_oop_turn, diffs: diffs_from_turn } = getClonedBetSizes({
    sizes: sizes_from_flop,
    street_from: 'turn',
    street_to: 'turn',
    player_from,
    player_to,
  })
  const { sizes: sizes_from_river, diffs: diffs_from_river } = getClonedBetSizes({
    sizes: sizes_oop_turn,
    street_from: 'river',
    street_to: 'river',
    player_from,
    player_to,
  })

  const sizes_final = sizes_from_river

  const diffs_total = diffs_from_flop.total + diffs_from_turn.total + diffs_from_river.total

  const res = {
    sizes_final,
    diffs_total,
    diffs_from_flop,
    diffs_from_turn,
    diffs_from_river,
  }
  return res
}

function getFormError({ formResult }) {
  const { pot, ante, starting_street } = formResult
  const stack = formResult.players[0]?.stack
  const smallBlind = formResult.players.find(({ position }) => position === 'IP').blind
  const conf = getSolfigrFields()

  const res =
    starting_street === 'PREFLOP'
      ? conf.stack.preflop_stackAnteBlindCheck({ stack, preflop_SB: smallBlind, ante })
      : conf.stack.flop_stackPotCheck({ stack, pot })

  return !res
}

function validateTournamentStructure({ payout_levels }) {
  let res = {
    error_index: [],
    error_payout_zero_prize: [],
    error_payout_bigger_prize_than_preceding_prize: [],
    total_errors: 0,
  }
  payout_levels.forEach((item, index) => {
    if (item.prize === 0) {
      res.error_index.push(index)
      res.error_payout_zero_prize.push(index)
      res.total_errors++
    }
    if (item.prize && item.prize > payout_levels[index - 1]?.prize) {
      res.error_index.push(index)
      res.error_payout_bigger_prize_than_preceding_prize.push(index)
      res.total_errors++
    }
  })

  return res
}

function getTournamentStructure(item) {
  const prize_pool = numFixed(
    item.payout_levels.reduce((acc, i) => acc + Number(i.prize || 0), 0),
    4,
  )

  const payout_levels = item.payout_levels.map(i => ({
    rank: i.rank,
    bounty: i.bounty, // not editable in UI - returned from API - must be returned as well
    prize: Number(i.prize),
    prize_pool_fraction: i.prize / prize_pool,
  }))

  const res = {
    type: item.type,
    name: item.name || '',
    total_entrants: item.total_entrants,
    bounty_instant_percentage: Number(item.bounty_instant_percentage),
    payout_levels,
    prize_pool,
    errors: validateTournamentStructure({ payout_levels }),
  }
  return res
}

function getIcmPlayersAndStacks({ tournamentData, icmHandDetails }) {
  const conf = getSolfigrFields()
  const stacksManual = tournamentData.lognormal_deviation === null
  const stacksDefined = tournamentData.lognormal_deviation !== null
  return {
    stacks_distribution_type: stacksManual ? 'sdt_manual' : 'sdt_lognormal',
    lognormal_deviation: Number(
      tournamentData.lognormal_deviation || conf.lognormal_deviation.default,
    ),
    num_of_players_remaining: Number(
      tournamentData.players_remaining ?? conf.num_of_players_remaining.default,
    ),
    avg_stack: Number(
      tournamentData.average_stack ??
        conf.avg_stack.getDefault({
          stacks: [
            icmHandDetails.stack_IP,
            icmHandDetails.stack_OOP,
            icmHandDetails.api_oop1_player && icmHandDetails.stack_OOP1,
          ].filter(Boolean),
        }),
    ),
    avg_bounty: Number(tournamentData.average_bounty ?? conf.avg_bounty.getDefault(icmHandDetails)),
    stacks_manual: stacksManual ? tournamentData.stacks : [],
    stacks_defined: stacksDefined ? tournamentData.stacks : [],
  }
}

function validateHandDetails({ stack_IP, stack_OOP, pot_size, stack_OOP1 = undefined }) {
  const conf = getSolfigrFields()
  const error_stack_OOP = !conf.stack.flop_stackPotCheck({
    stack: stack_OOP,
    pot: pot_size,
  })
  const error_stack_IP = !conf.stack.flop_stackPotCheck({
    stack: stack_IP,
    pot: pot_size,
  })

  const error_stack_OOP1 =
    stack_OOP1 &&
    !conf.stack.flop_stackPotCheck({
      stack: stack_OOP1,
      pot: pot_size,
    })

  return {
    total_errors: (error_stack_OOP ? 1 : 0) + (error_stack_IP ? 1 : 0) + (error_stack_OOP1 ? 1 : 0),
    error_stack_OOP,
    error_stack_IP,
    error_stack_OOP1,
  }
}

function getIcmHandDetails({
  pot,
  api_oop_player,
  api_ip_player,
  api_oop1_player,
  bounty_instant_percentage,
}) {
  const bounty_total_IP = Number(api_ip_player.tournament_total_bounty)
  const bounty_total_OOP = Number(api_oop_player.tournament_total_bounty)
  const bounty_total_OOP1 = api_oop1_player && Number(api_oop1_player.tournament_total_bounty)
  const pot_size = pot
  const stack_IP = Number(api_ip_player.stack)
  const stack_OOP = Number(api_oop_player.stack)
  const stack_OOP1 = api_oop1_player && Number(api_oop1_player.stack)
  return {
    pot_size,
    stack_IP,
    stack_OOP,
    stack_OOP1,
    bounty_total_IP,
    bounty_total_OOP,
    bounty_total_OOP1,
    bounty_instant_IP: numFixed((bounty_total_IP || 0) * (bounty_instant_percentage || 1), 4),
    bounty_instant_OOP: numFixed((bounty_total_OOP || 0) * (bounty_instant_percentage || 1), 4),
    bounty_instant_OOP1:
      api_oop1_player && numFixed((bounty_total_OOP1 || 0) * (bounty_instant_percentage || 1), 4),
    errors: validateHandDetails({ stack_IP, stack_OOP, stack_OOP1, pot_size }),
  }
}

function applyTournamentStructure({ item, namespace, store }) {
  const value = getTournamentStructure(item)
  store.commit(SNK('slfrm_set', namespace), { key: 'slfrm_icm_tournament', value })
  store.dispatch(SNK('slrfm_updateIcmInheritedValues', namespace), {
    icmTournament: value,
    getTreePreview: true,
  })
}

function getIcmTournamentSites() {
  return [
    '888(CA-ON)',
    '888Poker',
    '888Poker(ES-PT)',
    '888Poker.it',
    'Adda52',
    'BetMGM(MI)',
    'BetMGM(PA)',
    'Bodog',
    'Chico',
    'GGNetwork',
    'Global Poker',
    'IDNPoker',
    'MicroGame',
    'OK Poker',
    'PartyPoker',
    'PartyPoker(CA-ON)',
    'PartyPoker(FR-ES)',
    'PartyPoker(NJ)',
    'PartyPoker.it',
    'PlanetWin365-Pro-IT',
    'Pocket52',
    'PokerBaazi',
    'PokerStars',
    'PokerStars(CA-ON)',
    'PokerStars(FR-ES-PT)',
    'PokerStars(NJ-MI)',
    'PokerStars(PA)',
    'PokerStars.it',
    'Pokerbet',
    'Revolution',
    'SkyPoker',
    'Spartan Poker',
    'WPN',
    'WSOP(CA-ON)',
    'WSOP(MI-NJ-NV)',
    'WSOP(PA)',
    'Winamax.fr',
    'iPoker',
    'iPoker(FR-ES)',
    'iPoker.it',
  ]
}

function getAvgStackIpOopIncluded({ stackIP, stackOOP, avgStack, remainingPlayers, avgStackMin }) {
  const stacksInHand = stackIP + stackOOP
  const otherPlayers = remainingPlayers - 2 // (IP and OOP)
  let res = (avgStack * remainingPlayers - stacksInHand) / otherPlayers
  res = Math.max(res, avgStackMin)
  return isNaN(res) ? avgStackMin : res
}

export {
  getSolfigrFields,
  getHandsRangeFromGroupRange,
  getSolfigrFormApiPostData,
  getSolfigrCanRaises,
  getSolfigrPlayer,
  getGroupsRangeFromHandsRange,
  getRangeOverview,
  getSizesSkeleton,
  getClonedBetSizes,
  getSolfigrSizesFromApiData,
  getFlopOnTurnAndRiverDiffs,
  getPlayersCloningFirstPlayer,
  getSizesDiffsOnAllStreets,
  getFormError,
  getPreflopSizes,
  getBlinds,
  applyTournamentStructure,
  getIcmTournamentSites,
  getTournamentStructure,
  validateTournamentStructure,
  validateHandDetails,
  getIcmHandDetails,
  getIcmPlayersAndStacks,
  logposition,
  logslider,
  getAvgStackIpOopIncluded,
}
